import { useEffect, useMemo, useState } from "react";
import { getTendenciasEstudiante } from "../services/alertasGenericasService";
import { codigosSubcompetencia, etiquetaSubcompetencia, valorNormalizado } from "../utils/subcompetencias";

const CORREO_ESTUDIANTE_DEFECTO = "zurrea@cuc.edu.co";

const uniqSorted = (arr, mapFn = (x) => x) => {
  const set = new Set();
  (arr || []).forEach((item) => {
    const v = mapFn(item);
    if (v !== undefined && v !== null && v !== "") set.add(String(v));
  });
  return [...set].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === a && String(nb) === b) {
      return na - nb;
    }
    return String(a).localeCompare(String(b));
  });
};

const sortRowsByPeriodo = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const ya = Number(a.anio) || 0;
    const yb = Number(b.anio) || 0;
    if (ya !== yb) return ya - yb;
    const sa = Number(a.semestre) || 0;
    const sb = Number(b.semestre) || 0;
    if (sa !== sb) return sa - sb;
    const ia = Number(a.Intento) || 0;
    const ib = Number(b.Intento) || 0;
    if (ia !== ib) return ia - ib;
    return (Number(a.OrdenEvaluacion) || 0) - (Number(b.OrdenEvaluacion) || 0);
  });

const periodoKeyDesdePartes = (y, s) => `${y}-${s}`;

const parsePeriodoKey = (key) => {
  const s = String(key || "");
  const i = s.indexOf("-");
  if (i <= 0) return { y: 0, sem: 0 };
  const y = Number(s.slice(0, i));
  const sem = Number(s.slice(i + 1));
  return { y: Number.isFinite(y) ? y : 0, sem: Number.isFinite(sem) ? sem : 0 };
};

const cmpPeriodoKey = (ka, kb) => {
  const a = parsePeriodoKey(ka);
  const b = parsePeriodoKey(kb);
  if (a.y !== b.y) return a.y - b.y;
  return a.sem - b.sem;
};

const siguientePeriodoAcademico = (y, sem) => (sem === 1 ? [y, 2] : [y + 1, 1]);

const personaDesdePayload = (persona, correo) => {
  const p = persona || {};
  return {
    id: null,
    nombreCompleto: String(p.nombre || "").trim(),
    correo: correo || "",
    correo_personal: String(p.correo_personal || "").trim(),
    telefono: String(p.telefono || "").trim(),
    celular: String(p.celular || "").trim(),
    programa: String(p.programa || "").trim(),
  };
};

export const useTendencias = (competencia = "RC") => {
  const [filtroAnio, setFiltroAnio] = useState("");
  const [filtroSemestre, setFiltroSemestre] = useState("");
  const [filtroPrueba, setFiltroPrueba] = useState("");
  const [correo, setCorreo] = useState(CORREO_ESTUDIANTE_DEFECTO);

  const [todasFilasEstudiante, setTodasFilasEstudiante] = useState([]);
  const [alertasPrediccion, setAlertasPrediccion] = useState([]);

  const correoInicialCarga = (CORREO_ESTUDIANTE_DEFECTO || "").trim().includes("@");
  const [loadingEstudiante, setLoadingEstudiante] = useState(correoInicialCarga);

  const [personaDetalle, setPersonaDetalle] = useState(null);

  useEffect(() => {
    setFiltroAnio("");
    setFiltroSemestre("");
    setFiltroPrueba("");
  }, [correo]);

  useEffect(() => {
    if (!correo) {
      setTodasFilasEstudiante([]);
      setAlertasPrediccion([]);
      setPersonaDetalle(null);
      setLoadingEstudiante(false);
      return undefined;
    }

    const trimmed = (correo || "").trim();
    if (!trimmed.includes("@")) {
      setTodasFilasEstudiante([]);
      setAlertasPrediccion([]);
      setPersonaDetalle(null);
      setLoadingEstudiante(false);
      return undefined;
    }

    let activo = true;
    setLoadingEstudiante(true);

    getTendenciasEstudiante(trimmed, competencia).then((payload) => {
      if (!activo) return;
      const list = Array.isArray(payload?.filas) ? payload.filas : [];
      const preds = Array.isArray(payload?.predicciones) ? payload.predicciones : [];
      setTodasFilasEstudiante(list);
      setAlertasPrediccion(preds);
      setPersonaDetalle(personaDesdePayload(payload?.persona, trimmed));
      setLoadingEstudiante(false);
    });

    return () => {
      activo = false;
    };
  }, [correo, competencia]);

  const opcionesAnio = useMemo(() => uniqSorted(todasFilasEstudiante, (r) => r.anio), [todasFilasEstudiante]);

  const opcionesSemestre = useMemo(() => {
    if (!filtroAnio) return [];
    const base = todasFilasEstudiante.filter((r) => String(r.anio) === String(filtroAnio));
    return uniqSorted(base, (r) => r.semestre);
  }, [todasFilasEstudiante, filtroAnio]);

  const opcionesPrueba = useMemo(() => {
    let base = todasFilasEstudiante;
    if (filtroAnio) base = base.filter((r) => String(r.anio) === String(filtroAnio));
    if (filtroSemestre) base = base.filter((r) => String(r.semestre) === String(filtroSemestre));
    return uniqSorted(base, (r) => r.Prueba).filter(Boolean);
  }, [todasFilasEstudiante, filtroAnio, filtroSemestre]);

  const opcionesCorreo = useMemo(() => (correo ? [correo] : []), [correo]);

  useEffect(() => {
    if (filtroAnio && !opcionesAnio.includes(filtroAnio)) {
      setFiltroAnio("");
    }
  }, [opcionesAnio, filtroAnio]);

  useEffect(() => {
    if (filtroSemestre && !opcionesSemestre.includes(filtroSemestre)) {
      setFiltroSemestre("");
    }
  }, [opcionesSemestre, filtroSemestre]);

  useEffect(() => {
    if (filtroPrueba && !opcionesPrueba.includes(filtroPrueba)) {
      setFiltroPrueba("");
    }
  }, [opcionesPrueba, filtroPrueba]);

  const evolucionAnualCompleta = useMemo(() => {
    const rows = todasFilasEstudiante;
    if (!Array.isArray(rows) || !rows.length) return [];
    const byPeriod = new Map();
    rows.forEach((r) => {
      const y = r.anio != null && String(r.anio).trim() !== "" ? String(r.anio).trim() : "";
      const sem = r.semestre != null && String(r.semestre).trim() !== "" ? String(r.semestre).trim() : "";
      if (!y || !sem) return;
      const key = `${y}-${sem}`;
      if (!byPeriod.has(key)) byPeriod.set(key, { sum: 0, c: 0 });
      const b = byPeriod.get(key);
      b.sum += Number(r.Total) || 0;
      b.c += 1;
    });
    const keys = [...byPeriod.keys()].sort(cmpPeriodoKey);
    if (!keys.length) return [];
    const first = parsePeriodoKey(keys[0]);
    const last = parsePeriodoKey(keys[keys.length - 1]);
    const out = [];
    let cy = first.y;
    let csem = first.sem;
    while (true) {
      const key = periodoKeyDesdePartes(cy, csem);
      if (byPeriod.has(key)) {
        const b = byPeriod.get(key);
        out.push({
          anio: String(cy),
          semestre: String(csem),
          periodo: key,
          puntaje: Number((b.sum / b.c).toFixed(2)),
        });
      } else {
        out.push({
          anio: String(cy),
          semestre: String(csem),
          periodo: key,
          puntaje: null,
        });
      }
      if (cy === last.y && csem === last.sem) break;
      [cy, csem] = siguientePeriodoAcademico(cy, csem);
    }
    return out;
  }, [todasFilasEstudiante]);

  const filasOrdenadas = useMemo(() => {
    let rows = todasFilasEstudiante;
    if (filtroAnio) rows = rows.filter((r) => String(r.anio) === String(filtroAnio));
    if (filtroSemestre) rows = rows.filter((r) => String(r.semestre) === String(filtroSemestre));
    if (filtroPrueba) rows = rows.filter((r) => String(r.Prueba) === String(filtroPrueba));
    return sortRowsByPeriodo(rows);
  }, [todasFilasEstudiante, filtroAnio, filtroSemestre, filtroPrueba]);

  const metricas = useMemo(() => {
    if (!filasOrdenadas.length) {
      return {
        promedioTotal: null,
        mejorCompetencia: null,
        ultimoIntento: null,
        totalIntentos: 0,
        radar: [],
      };
    }

    const n = filasOrdenadas.length;
    const sumTotal = filasOrdenadas.reduce((s, r) => s + (Number(r.Total) || 0), 0);
    const promedioTotal = sumTotal / n;

    // Las subcompetencias varían por competencia (RC/LC: C1-C3, CC: C1-C7, CE: ninguna):
    // se descubren por fila en vez de asumir C1-C3.
    const codigos = codigosSubcompetencia(filasOrdenadas);
    const comps = codigos.map((codigo) => {
      const suma = filasOrdenadas.reduce((s, r) => s + valorNormalizado(r, codigo), 0);
      return {
        key: codigo,
        label: `${codigo}: ${etiquetaSubcompetencia(filasOrdenadas[0], codigo)}`,
        prom: suma / n,
      };
    });
    const mejor = comps.length ? [...comps].sort((a, b) => b.prom - a.prom)[0] : null;

    const ultimo = filasOrdenadas[filasOrdenadas.length - 1];
    const ultimoIntento = Number(ultimo.Total);

    const radar = codigos.map((codigo) => ({
      subject: codigo,
      value: valorNormalizado(ultimo, codigo),
      fullMark: 5,
    }));

    return {
      promedioTotal: Number(promedioTotal.toFixed(2)),
      mejorCompetencia: mejor,
      ultimoIntento: Number.isFinite(ultimoIntento) ? Number(ultimoIntento.toFixed(2)) : null,
      totalIntentos: n,
      radar,
    };
  }, [filasOrdenadas]);

  const recomendaciones = useMemo(() => {
    if (!alertasPrediccion.length) return { desemp: "", cuc: "" };
    const row = alertasPrediccion[0];
    return {
      desemp: row.recomendacion_desemp || "",
      cuc: row.recomendacion_cuc || "",
    };
  }, [alertasPrediccion]);

  return {
    filtroAnio,
    setFiltroAnio,
    filtroSemestre,
    setFiltroSemestre,
    filtroPrueba,
    setFiltroPrueba,
    correo,
    setCorreo,
    opcionesAnio,
    opcionesSemestre,
    opcionesCorreo,
    opcionesPrueba,
    filasOrdenadas,
    metricas,
    recomendaciones,
    loadingEstudiante,
    cargandoVistaCompleta: Boolean((correo || "").trim().includes("@")) && loadingEstudiante,
    tieneHistorialEstudiante: todasFilasEstudiante.length > 0,
    personaDetalle,
    evolucionAnualCompleta,
  };
};
